/**
 * Which liabilities CAPITALISE rather than being serviced out of the client's cashflow.
 *
 * A reverse mortgage and a Home Equity Access Scheme loan are both repaid out of the property when
 * it is eventually sold, not out of income. Nothing leaves the bank for them each month, so giving
 * one a repayment schedule would show a client money going out that they never actually pay — and
 * would double-count against a balance that is meanwhile growing on its own.
 *
 * ONE DEFINITION, because there are three separate places that need it: the repayment assessor
 * (`loanRepayments.ts`), the structure-liability repayment helper, and the projection accrual that
 * compounds these balances (`loanPlugin.ts`). Until 2026-08-22 the first two each tested
 * `type === 'reverse_mortgage'` inline, which was the only value that existed then. When existing
 * reverse mortgages and HEAS loans became recordable as ordinary liabilities — they previously had
 * nowhere to live but a Living Arrangements question that no calculation ever read — those inline
 * tests would have missed the new category ids and handed both a repayment schedule they never pay.
 *
 * `reverse_mortgage` and `heas` are the ids the STRATEGY modeller injects; `reverseMortgages` and
 * `homeEquityAccessScheme` are the LIABILITY_TYPES category ids a client records against. Both
 * spellings resolve, so a strategy-injected loan and a client-recorded one behave identically.
 */
export const CAPITALISING_LOAN_TYPES: ReadonlySet<string> = new Set([
    'reverse_mortgage',
    'reverseMortgages',
    'heas',
    'homeEquityAccessScheme',
]);

/** True when this liability's interest capitalises rather than being paid out of cashflow. */
export function isCapitalisingLoan(liability: { type?: string | null } | null | undefined): boolean {
    return CAPITALISING_LOAN_TYPES.has(String(liability?.type || ''));
}

/**
 * THE reverse mortgage rate, when nobody has typed one — 8.80% p.a.
 *
 * Until 2026-09-12 there were three: 7.5% here and on the fact-find form, 8.0% in the manual-control
 * panel, the suggestion engine and the projection's own fallback, and 6.5% on the loan the projection
 * takes out by itself when a client's money runs out. An adviser opening manual control saw a
 * different default from the one their fact-find had filled in, and the shortfall loan — the one
 * clients actually end up on — was the cheapest of the lot. Every one of those sites now reads this.
 *
 * 8.80% is the owner's figure for a commercial reverse mortgage in 2026: above the 7.5–8% that had
 * been assumed, which understated a debt that nobody repays and that therefore compounds in full.
 * It is a default, shown in the field and meant to be edited per lender; it is not a promise.
 */
export const REVERSE_MORTGAGE_DEFAULT_RATE = 8.8;

/** The Home Equity Access Scheme rate, set by government. */
export const HEAS_RATE = 3.95;

/**
 * The rate a capitalising loan accrues at when the client has not recorded one.
 *
 * SHARED with the fact-find on purpose. The form shows this figure in the Interest Rate field when
 * the client leaves it blank, so the rate on screen is always the rate being modelled. Showing an
 * empty box while quietly compounding at 3.95% is the same form-says-one-thing-engine-does-another
 * mismatch that had these loans asking for repayments they never make.
 *
 * Neither may be read as 0%: these loans always accrue, so a blank must never mean "no interest" —
 * which is what a plain `|| 0` would have produced.
 */
export const CAPITALISING_LOAN_DEFAULT_RATE: Record<string, number> = {
    reverse_mortgage: REVERSE_MORTGAGE_DEFAULT_RATE,
    reverseMortgages: REVERSE_MORTGAGE_DEFAULT_RATE,
    heas: HEAS_RATE,
    homeEquityAccessScheme: HEAS_RATE,
};

/** The rate this loan accrues at: the client's own figure, or the default for its kind. */
export function capitalisingLoanRateOf(liability: { type?: string | null; interestRate?: number | null } | null | undefined): number {
    const recorded = liability?.interestRate;
    if (typeof recorded === 'number' && Number.isFinite(recorded)) return recorded;
    return CAPITALISING_LOAN_DEFAULT_RATE[String(liability?.type || '')] ?? REVERSE_MORTGAGE_DEFAULT_RATE;
}
